import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { EpisodeService, SettingsService } from '../../core/services';
import { EpisodeListItem } from '../../core/models';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  latestEpisodes: EpisodeListItem[] = [];
  isLoading = true;
  error: string | null = null;
  defaultCoverUrl: string | null = null;
  rssUrl = `${environment.apiUrl}/rss/feed.xml`;

  constructor(
    private episodeService: EpisodeService,
    private settingsService: SettingsService,
    private meta: Meta,
    private title: Title
  ) {}

  ngOnInit(): void {
    console.log('HomeComponent ngOnInit called');
    
    // Imposta i meta tag per la home
    this.title.setTitle('AI PodLab - Il podcast su scienza e tecnologia');
    this.meta.updateTag({ name: 'description', content: 'Esplora il mondo della scienza e della tecnologia con AI PodLab. Episodi settimanali su intelligenza artificiale, innovazione e scoperte scientifiche.' });
    this.meta.updateTag({ property: 'og:title', content: 'AI PodLab - Il podcast su scienza e tecnologia' });
    this.meta.updateTag({ property: 'og:description', content: 'Esplora il mondo della scienza e della tecnologia con AI PodLab. Episodi settimanali su intelligenza artificiale, innovazione e scoperte scientifiche.' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    
    // Carica prima le impostazioni, poi gli episodi
    this.settingsService.getPublicSettings().subscribe({
      next: (response) => {
        if (response.data.default_cover) {
          const baseUrl = environment.apiUrl.replace('/api', '');
          const coverPath = response.data.default_cover;
          // Se il path non inizia con /, aggiungilo insieme a /media/covers/
          if (coverPath.startsWith('/')) {
            this.defaultCoverUrl = `${baseUrl}${coverPath}`;
          } else {
            this.defaultCoverUrl = `${baseUrl}/media/covers/${coverPath}`;
          }
          console.log('Default cover URL:', this.defaultCoverUrl);
        }
        // Ora carica gli episodi
        this.loadLatestEpisodes();
      },
      error: (err) => {
        console.error('Error loading settings:', err);
        // Carica comunque gli episodi anche se le impostazioni falliscono
        this.loadLatestEpisodes();
      }
    });
  }

  ngOnDestroy(): void {
    console.log('HomeComponent ngOnDestroy called');
  }

  /**
   * Carica gli ultimi episodi
   */
  loadLatestEpisodes(): void {
    this.isLoading = true;
    this.error = null;

    this.episodeService.getLatest(6).subscribe({
      next: (response) => {
        console.log('Response from API:', response);
        // Mappa i dati dal formato backend al formato frontend
        this.latestEpisodes = this.mapEpisodesFromBackend(response.data);
        console.log('Mapped episodes:', this.latestEpisodes);
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading episodes:', err);
        this.error = 'Impossibile caricare gli episodi. Riprova più tardi.';
        this.isLoading = false;
      }
    });
  }

  /**
   * Mappa i dati dal formato backend (snake_case) al formato frontend (camelCase)
   */
  private mapEpisodesFromBackend(backendData: any[]): EpisodeListItem[] {
    return backendData.map(episode => {
      // Parse tag_names se è una stringa JSON
      let tagNames: string[] = [];
      if (episode.tag_names) {
        if (typeof episode.tag_names === 'string') {
          try {
            const parsed = JSON.parse(episode.tag_names);
            tagNames = Array.isArray(parsed) ? parsed.filter((t: any) => t !== null) : [];
          } catch {
            tagNames = [];
          }
        } else if (Array.isArray(episode.tag_names)) {
          tagNames = episode.tag_names.filter((t: any) => t !== null);
        }
      }

      return {
        id: episode.id,
        title: episode.title,
        slug: episode.slug,
        description: episode.description,
        duration: episode.audio_duration || 0,
        publishedAt: new Date(episode.published_at),
        coverImage: episode.cover_image,
        categoryName: episode.category_name || 'Uncategorized',
        tagNames: tagNames
      };
    });
  }

  /**
   * Ottiene l'URL completo dell'immagine di copertina
   * @param coverImage Nome file immagine
   * @param size Dimensione thumbnail: 'small' | 'medium' | 'large' | undefined (original)
   */
  getCoverImageUrl(coverImage: string | undefined, size?: 'small' | 'medium' | 'large'): string {
    if (!coverImage) {
      if (this.defaultCoverUrl) {
        return this.defaultCoverUrl;
      }
      return 'https://via.placeholder.com/800x450/667eea/ffffff?text=PodLab';
    }
    // Se l'URL inizia con http, è già completo
    if (coverImage.startsWith('http')) {
      return coverImage;
    }
    
    // Costruisci l'URL base rimuovendo /api dalla fine
    let baseUrl = environment.apiUrl;
    if (baseUrl.endsWith('/api')) {
      baseUrl = baseUrl.slice(0, -4); // Rimuove '/api'
    }
    
    // Se richiede un thumbnail specifico
    if (size) {
      const ext = coverImage.substring(coverImage.lastIndexOf('.'));
      const nameWithoutExt = coverImage.substring(0, coverImage.lastIndexOf('.'));
      const thumbnailPath = `thumbs/${nameWithoutExt}-${size}${ext}`;
      
      if (thumbnailPath.startsWith('/')) {
        return `${baseUrl}${thumbnailPath}`;
      }
      return `${baseUrl}/media/covers/${thumbnailPath}`;
    }
    
    // Altrimenti usa l'originale
    // Se il path inizia con /, usalo così com'è
    if (coverImage.startsWith('/')) {
      return `${baseUrl}${coverImage}`;
    }
    // Altrimenti aggiungi /media/covers/ prima del filename
    return `${baseUrl}/media/covers/${coverImage}`;
  }

  /**
   * Formatta la durata da secondi a mm:ss
   */
  formatDuration(seconds: number): string {
    if (!seconds || isNaN(seconds)) {
      return '0:00';
    }
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Formatta la data in formato leggibile
   */
  formatDate(date: Date | string): string {
    if (!date) {
      return 'Data non disponibile';
    }
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      return 'Data non disponibile';
    }
    return dateObj.toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
}
